// Audit Service for TyreLink Communication System
// Handles logging of all communication activities for compliance and monitoring

import { supabase } from './supabase'
import type {
  CommunicationAuditLog,
  CreateCommunicationAuditLog,
  AuditAction,
  EntityType,
  MessagingServiceResponse
} from '../types/messaging'

class AuditService {
  async logActivity(auditData: CreateCommunicationAuditLog): Promise<MessagingServiceResponse<CommunicationAuditLog>> {
    try {
      const { data, error } = await supabase
        .from('communication_audit_log')
        .insert([auditData])
        .select()
        .single()

      if (error) {
        // Don't fail the main operation if audit logging fails
        console.error('Audit logging failed:', error)
        return {
          success: false,
          error: {
            code: 'MESSAGE_FAILED',
            message: error.message
          }
        }
      }

      return {
        success: true,
        data
      }
    } catch (error) {
      console.error('Audit logging error:', error)
      return {
        success: false,
        error: {
          code: 'MESSAGE_FAILED',
          message: 'Failed to log audit activity',
          details: error
        }
      }
    }
  }

  async logThreadActivity(
    action: AuditAction,
    threadId: string,
    userId?: string,
    oldValues?: Record<string, any>,
    newValues?: Record<string, any>,
    request?: Request
  ): Promise<void> {
    const auditData: CreateCommunicationAuditLog = {
      user_id: userId,
      thread_id: threadId,
      action,
      entity_type: 'thread',
      entity_id: threadId,
      old_values: oldValues,
      new_values: newValues,
      ip_address: this.getClientIP(request),
      user_agent: this.getUserAgent(request)
    }

    await this.logActivity(auditData)
  }

  async logMessageActivity(
    action: AuditAction,
    messageId: string,
    threadId?: string,
    userId?: string,
    oldValues?: Record<string, any>,
    newValues?: Record<string, any>,
    request?: Request
  ): Promise<void> {
    const auditData: CreateCommunicationAuditLog = {
      user_id: userId,
      thread_id: threadId,
      message_id: messageId,
      action,
      entity_type: 'message',
      entity_id: messageId,
      old_values: oldValues,
      new_values: newValues,
      ip_address: this.getClientIP(request),
      user_agent: this.getUserAgent(request)
    }

    await this.logActivity(auditData)
  }

  async logNotificationActivity(
    action: AuditAction,
    notificationId: string,
    userId?: string,
    oldValues?: Record<string, any>,
    newValues?: Record<string, any>,
    request?: Request
  ): Promise<void> {
    const auditData: CreateCommunicationAuditLog = {
      user_id: userId,
      action,
      entity_type: 'notification',
      entity_id: notificationId,
      old_values: oldValues,
      new_values: newValues,
      ip_address: this.getClientIP(request),
      user_agent: this.getUserAgent(request)
    }

    await this.logActivity(auditData)
  }

  async logPreferenceActivity(
    action: AuditAction,
    userId: string,
    oldValues?: Record<string, any>,
    newValues?: Record<string, any>,
    request?: Request
  ): Promise<void> {
    const auditData: CreateCommunicationAuditLog = {
      user_id: userId,
      action,
      entity_type: 'preference',
      entity_id: userId,
      old_values: oldValues,
      new_values: newValues,
      ip_address: this.getClientIP(request),
      user_agent: this.getUserAgent(request)
    }

    await this.logActivity(auditData)
  }

  // Convenient logging methods for common actions
  async logThreadCreated(threadId: string, userId: string, threadData: Record<string, any>, request?: Request): Promise<void> {
    await this.logThreadActivity('create', threadId, userId, undefined, threadData, request)
  }

  async logThreadUpdated(
    threadId: string, 
    userId: string, 
    oldData: Record<string, any>, 
    newData: Record<string, any>, 
    request?: Request
  ): Promise<void> {
    await this.logThreadActivity('update', threadId, userId, oldData, newData, request)
  }

  async logThreadDeleted(threadId: string, userId: string, threadData: Record<string, any>, request?: Request): Promise<void> {
    await this.logThreadActivity('delete', threadId, userId, threadData, undefined, request)
  }

  async logMessageSent(
    messageId: string, 
    threadId: string, 
    userId: string, 
    messageData: Record<string, any>, 
    request?: Request
  ): Promise<void> {
    await this.logMessageActivity('create', messageId, threadId, userId, undefined, messageData, request)
  }

  async logMessageRead(
    messageId: string, 
    threadId: string, 
    userId: string, 
    readData: Record<string, any>, 
    request?: Request
  ): Promise<void> {
    await this.logMessageActivity('read', messageId, threadId, userId, undefined, readData, request)
  }

  async logMessageUpdated(
    messageId: string,
    threadId: string,
    userId: string,
    oldData: Record<string, any>,
    newData: Record<string, any>,
    request?: Request
  ): Promise<void> {
    await this.logMessageActivity('update', messageId, threadId, userId, oldData, newData, request)
  }

  async logMessageDeleted(
    messageId: string,
    threadId: string,
    userId: string,
    messageData: Record<string, any>,
    request?: Request
  ): Promise<void> {
    await this.logMessageActivity('delete', messageId, threadId, userId, messageData, undefined, request)
  }

  async logNotificationSent(
    notificationId: string,
    userId: string,
    notificationData: Record<string, any>,
    request?: Request
  ): Promise<void> {
    await this.logNotificationActivity('send_notification', notificationId, userId, undefined, notificationData, request)
  }

  async logPreferencesUpdated(
    userId: string,
    oldPreferences: Record<string, any>,
    newPreferences: Record<string, any>,
    request?: Request
  ): Promise<void> {
    await this.logPreferenceActivity('update', userId, oldPreferences, newPreferences, request)
  }

  // Query audit logs
  async getAuditLogs(
    filters: {
      user_id?: string
      thread_id?: string
      message_id?: string
      action?: AuditAction
      entity_type?: EntityType
      start_date?: string
      end_date?: string
      limit?: number
      offset?: number
    }
  ): Promise<MessagingServiceResponse<CommunicationAuditLog[]>> {
    try {
      let query = supabase
        .from('communication_audit_log')
        .select('*')
        .order('created_at', { ascending: false })

      // Apply filters
      if (filters.user_id) {
        query = query.eq('user_id', filters.user_id)
      }

      if (filters.thread_id) {
        query = query.eq('thread_id', filters.thread_id)
      }

      if (filters.message_id) {
        query = query.eq('message_id', filters.message_id)
      }

      if (filters.action) {
        query = query.eq('action', filters.action)
      }

      if (filters.entity_type) {
        query = query.eq('entity_type', filters.entity_type)
      }

      if (filters.start_date) {
        query = query.gte('created_at', filters.start_date)
      }

      if (filters.end_date) {
        query = query.lte('created_at', filters.end_date)
      }

      if (filters.limit) {
        query = query.limit(filters.limit)
      }

      if (filters.offset) {
        query = query.range(filters.offset, filters.offset + (filters.limit || 50) - 1)
      }

      const { data, error } = await query

      if (error) {
        return {
          success: false,
          error: {
            code: 'MESSAGE_FAILED',
            message: error.message
          }
        }
      }

      return {
        success: true,
        data: data || []
      }
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'MESSAGE_FAILED',
          message: 'Failed to fetch audit logs',
          details: error
        }
      }
    }
  }

  async getUserAuditHistory(
    userId: string,
    days: number = 30,
    limit: number = 100
  ): Promise<MessagingServiceResponse<CommunicationAuditLog[]>> {
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - days)

    return this.getAuditLogs({
      user_id: userId,
      start_date: startDate.toISOString(),
      limit
    })
  }

  async getThreadAuditHistory(
    threadId: string,
    limit: number = 100
  ): Promise<MessagingServiceResponse<CommunicationAuditLog[]>> {
    return this.getAuditLogs({
      thread_id: threadId,
      limit
    })
  }

  async getMessageAuditHistory(
    messageId: string,
    limit: number = 50
  ): Promise<MessagingServiceResponse<CommunicationAuditLog[]>> {
    return this.getAuditLogs({
      message_id: messageId,
      limit
    })
  }

  // Analytics and reporting
  async getActivitySummary(
    userId?: string,
    startDate?: string,
    endDate?: string
  ): Promise<MessagingServiceResponse<{
    total_activities: number
    activities_by_action: Record<AuditAction, number>
    activities_by_entity: Record<EntityType, number>
    daily_activity: Array<{ date: string; count: number }>
  }>> {
    try {
      const filters: any = {}
      if (userId) filters.user_id = userId
      if (startDate) filters.start_date = startDate
      if (endDate) filters.end_date = endDate

      const logsResult = await this.getAuditLogs({ ...filters, limit: 10000 })
      
      if (!logsResult.success || !logsResult.data) {
        return {
          success: false,
          error: logsResult.error || {
            code: 'MESSAGE_FAILED',
            message: 'Failed to get activity data'
          }
        }
      }

      const logs = logsResult.data

      // Calculate summary statistics
      const activitiesByAction: Record<string, number> = {}
      const activitiesByEntity: Record<string, number> = {}
      const dailyActivity: Record<string, number> = {}

      logs.forEach(log => {
        // Count by action
        activitiesByAction[log.action] = (activitiesByAction[log.action] || 0) + 1
        
        // Count by entity type
        activitiesByEntity[log.entity_type] = (activitiesByEntity[log.entity_type] || 0) + 1
        
        // Count by day
        const date = log.created_at.split('T')[0]
        dailyActivity[date] = (dailyActivity[date] || 0) + 1
      })

      // Convert daily activity to array format
      const dailyActivityArray = Object.entries(dailyActivity).map(([date, count]) => ({
        date,
        count
      })).sort((a, b) => a.date.localeCompare(b.date))

      return {
        success: true,
        data: {
          total_activities: logs.length,
          activities_by_action: activitiesByAction as Record<AuditAction, number>,
          activities_by_entity: activitiesByEntity as Record<EntityType, number>,
          daily_activity: dailyActivityArray
        }
      }
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'MESSAGE_FAILED',
          message: 'Failed to generate activity summary',
          details: error
        }
      }
    }
  }

  // Helper methods for extracting request information
  private getClientIP(request?: Request): string | undefined {
    if (!request) return undefined
    
    // Try various headers that might contain the real IP
    const forwardedFor = request.headers.get('x-forwarded-for')
    if (forwardedFor) {
      return forwardedFor.split(',')[0].trim()
    }
    
    const realIP = request.headers.get('x-real-ip')
    if (realIP) {
      return realIP
    }
    
    const cfConnectingIP = request.headers.get('cf-connecting-ip')
    if (cfConnectingIP) {
      return cfConnectingIP
    }

    return undefined
  }

  private getUserAgent(request?: Request): string | undefined {
    if (!request) return undefined
    return request.headers.get('user-agent') || undefined
  }

  // Data retention and cleanup
  async cleanupOldLogs(retentionDays: number = 365): Promise<MessagingServiceResponse<{ deleted_count: number }>> {
    try {
      const cutoffDate = new Date()
      cutoffDate.setDate(cutoffDate.getDate() - retentionDays)

      // First count how many records will be deleted
      const { count, error: countError } = await supabase
        .from('communication_audit_log')
        .select('*', { count: 'exact', head: true })
        .lt('created_at', cutoffDate.toISOString())

      if (countError) {
        return {
          success: false,
          error: {
            code: 'MESSAGE_FAILED',
            message: countError.message
          }
        }
      }

      // Then delete the records
      const { error } = await supabase
        .from('communication_audit_log')
        .delete()
        .lt('created_at', cutoffDate.toISOString())

      if (error) {
        return {
          success: false,
          error: {
            code: 'MESSAGE_FAILED',
            message: error.message
          }
        }
      }

      return {
        success: true,
        data: { deleted_count: count || 0 }
      }
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'MESSAGE_FAILED',
          message: 'Failed to cleanup old audit logs',
          details: error
        }
      }
    }
  }
}

export const auditService = new AuditService()
export default auditService